import React, {useEffect, useRef, useState} from "react"
import {Navigate, useNavigate} from "react-router-dom"
import {useTranslation} from "react-i18next"
import {useConfig} from "../../hooks/useConfig"
import {useBoothSession} from "../../hooks/useBoothSession"
import {processAiEffect, removeBackground} from "../../library/requests/AiRequests"
import {Fade, Stack, Typography} from "@mui/material"
import Spinner from "../common/Spinner"

const PROCESSING_MESSAGE_COUNT = 7

function ProcessingPage() {
  const navigate = useNavigate()
  const {t} = useTranslation()

  const config = useConfig()
  const {rawImage, guestInputValues, setAiProcessedImage, addAiImage, clearAiImages} = useBoothSession()

  const hasStarted = useRef(false)

  const [usedIndices, setUsedIndices] = useState<number[]>(() => {
    const first = Math.floor(Math.random() * PROCESSING_MESSAGE_COUNT) + 1
    return [first]
  })
  const processingIndex = usedIndices[usedIndices.length - 1]

  useEffect(() => {
    const interval = setInterval(() => {
      setUsedIndices((prev) => {
        const remaining = Array.from({length: PROCESSING_MESSAGE_COUNT}, (_, i) => i + 1)
          .filter((i) => !prev.includes(i))
        if (remaining.length === 0) return prev
        const next = remaining[Math.floor(Math.random() * remaining.length)]
        return [...prev, next]
      })
    }, 5000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (!rawImage || hasStarted.current) return
    hasStarted.current = true

    if (config.aiEffectOn) {
      clearAiImages()
      processAiEffect(config.presetCode, rawImage, guestInputValues)
        .then((imageUrl) => addAiImage(imageUrl))
        .finally(() => navigate(`/${config.presetCode}/edit`))
    } else if (config.backgroundReplacementOn) {
      removeBackground(config.presetCode, rawImage)
        .then((imageUrl) => setAiProcessedImage(imageUrl))
        .catch(() => {})
        .finally(() => navigate(`/${config.presetCode}/edit`))
    } else {
      navigate(`/${config.presetCode}/edit`)
    }
  }, [rawImage, config, guestInputValues, clearAiImages, addAiImage, setAiProcessedImage, navigate])

  if (!rawImage) {
    return (
      <Navigate
        to={`/${config.presetCode}/camera`}
        replace
      />
    )
  }

  const theme = config.theme

  return (
    <Stack alignItems="center" spacing={2} sx={{mt: 5}}>
      <Spinner color={theme.textStyle.color}/>
      <Fade key={processingIndex} in timeout={1000}>
        <Typography sx={{...theme.textStyle, fontSize: "1.5rem"}}>
          {t(`guestInput.processing${processingIndex}`)}
        </Typography>
      </Fade>
    </Stack>
  )
}

export default ProcessingPage
